/**
 * @fileoverview Validated, typed access to the runtime environment.
 * @module config/env
 *
 * @description
 * `import.meta.env` is read exactly once, here. Everywhere else imports `env`,
 * so an unset variable fails loudly at boot instead of producing `undefined`
 * deep inside a component.
 */

/** Supported runtime labels. */
export const RuntimeEnv = {
	DEVELOPMENT: "development",
	TEST: "test",
	STAGING: "staging",
	PRODUCTION: "production",
} as const

/** Union of every supported runtime label. */
export type RuntimeEnv = (typeof RuntimeEnv)[keyof typeof RuntimeEnv]

const RUNTIME_VALUES: readonly string[] = Object.values(RuntimeEnv)

/**
 * Narrows a raw string to a known runtime label.
 *
 * @param value - Raw value read from the environment.
 * @param fallback - Value used when the input is absent or unknown.
 * @returns A valid runtime label.
 */
function parseRuntime(
	value: string | undefined,
	fallback: RuntimeEnv
): RuntimeEnv {
	if (value !== undefined && RUNTIME_VALUES.includes(value)) {
		return value as RuntimeEnv
	}
	return fallback
}

const runtime = parseRuntime(
	import.meta.env.VITE_NODE_ENV,
	import.meta.env.DEV ? RuntimeEnv.DEVELOPMENT : RuntimeEnv.PRODUCTION
)

/** The validated environment, frozen so nothing can mutate it at runtime. */
export const env = Object.freeze({
	/** Current runtime label. */
	runtime,
	/** True in local development. */
	isDevelopment: runtime === RuntimeEnv.DEVELOPMENT,
	/** True in production builds. */
	isProduction: runtime === RuntimeEnv.PRODUCTION,
})

/** Shape of the validated environment. */
export type Env = typeof env
